import { memo, ReactNode } from 'react'
import { Box, ButtonBase, Typography } from '@mui/material'
import ThinArrowRightIcon from '../../assets/icons/ThinArrowRightIcon'

type Props = {
    icon: ReactNode
    title: string
    description?: ReactNode
    onClick?: () => void
    /** Renders the row as an external link (new tab). */
    href?: string
    disabled?: boolean
    /** Replaces the trailing chevron. */
    trailing?: ReactNode
    'data-event'?: string
    'data-event-intent'?: string
}

/**
 * Boxed choice row: icon tile, title, one-line description, chevron.
 * Used for the intent list on the home screen and the channels on Contact us.
 */
const SupportRow = ({ icon, title, description, onClick, href, disabled, trailing, ...dataProps }: Props) => (
    <ButtonBase
        {...(href ? { component: 'a', href, target: '_blank', rel: 'noopener noreferrer' } : {})}
        onClick={onClick}
        disabled={disabled}
        {...dataProps}
        sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            textAlign: 'left',
            gap: 1.25,
            px: 1.25,
            py: 1,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1.5,
            color: 'text.primary',
            textDecoration: 'none',
            transition: 'border-color 0.15s, background-color 0.15s',
            '&:hover': { borderColor: 'text.disabled', bgcolor: 'action.hover' },
            '&.Mui-focusVisible': { borderColor: 'text.primary' },
            '&.Mui-disabled': { color: 'text.disabled' },
            '&.Mui-disabled .support-row-icon': { color: 'text.disabled' },
            '&.Mui-disabled .support-row-description': { color: 'text.disabled' }
        }}
    >
        <Box
            className="support-row-icon"
            sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                bgcolor: 'action.hover',
                color: 'text.secondary',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                '& svg': { fontSize: 20 }
            }}
        >
            {icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" fontWeight={500} noWrap>
                {title}
            </Typography>
            {description ? (
                <Typography
                    className="support-row-description"
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', lineHeight: 1.4, mt: 0.25 }}
                >
                    {description}
                </Typography>
            ) : null}
        </Box>
        {trailing ?? <ThinArrowRightIcon sx={{ fontSize: 18, color: 'text.tertiary', flexShrink: 0 }} />}
    </ButtonBase>
)

export default memo(SupportRow)
